import OrthogonalRouter, {
  type ConnectorPoint,
} from '../../utils/OrthogonalRouter';
import { pointNearLine } from '../../utils/geometry';
import { fabric } from 'fabric';
import RouterStraight from './RouterStraight';
import { ConnectorEnd, type MenuDirection } from '../../types';
import type Connector from '../Connector';
import { type IPoint } from 'fabric/fabric-impl';
import { ConnectorRouting } from '../ConnectorRouting';

function round(v: number) {
  return Math.round(v * 1000) / 1000;
}

// Maximum routing margin around shapes.
const SHAPE_MARGIN = 20;
const DEFAULT_CORNER_RADIUS = 5;

// Router for creating orthogonal lines.
//
// `points` holds the endpoints of the entire line.
// `controlPoints` holds the endpoints of each segment of the line.
class RouterOrthogonal {
  static computeControlPoints(connector: Connector) {
    if (connector.lineRouting === ConnectorRouting.ROUTING_ORTHOGONAL_MANUAL) {
      connector.controlPoints = connector.points;
      return;
    }

    const router = new OrthogonalRouter();

    const endPoints: IPoint[] = [];
    const routerPoints: ConnectorPoint[] = [];

    endPoints[ConnectorEnd.CONNECTOR_START] = connector.points[0];
    endPoints[ConnectorEnd.CONNECTOR_END] =
      connector.points[connector.points.length - 1];

    for (const whichEnd of [
      ConnectorEnd.CONNECTOR_START,
      ConnectorEnd.CONNECTOR_END,
    ]) {
      const attachment = connector.attachments[whichEnd];

      if (attachment?.object) {
        const boundingBox = attachment.object.getBoundingRect(true, true);
        routerPoints[whichEnd] = {
          point: { x: endPoints[whichEnd].x, y: endPoints[whichEnd].y },
          shape: {
            left: boundingBox.left,
            top: boundingBox.top,
            width: boundingBox.width,
            height: boundingBox.height,
          },
          side: this._normalToSide(attachment.object.angle!, attachment.normal),
        };
      } else {
        routerPoints[whichEnd] = {
          point: {
            x: round(endPoints[whichEnd].x),
            y: round(endPoints[whichEnd].y),
          },
          shape: {
            left: round(endPoints[whichEnd].x),
            top: round(endPoints[whichEnd].y),
            width: 0,
            height: 0,
          },
          side: this._closestSide(
            endPoints[whichEnd],
            endPoints[
              (connector.constructor as typeof Connector).otherEnd(whichEnd)
            ]
          ), // Orient towards other end of line.
        };
      }
    }

    const routePoints = router.route({
      pointA: routerPoints[ConnectorEnd.CONNECTOR_START],
      pointB: routerPoints[ConnectorEnd.CONNECTOR_END],
      shapeMargin: SHAPE_MARGIN,
      globalBounds: {
        left: -100000,
        top: -100000,
        width: 200000,
        height: 200000,
      },
    });
    if (connector.canvas) connector.canvas.traceConnector = router.byproduct;

    const oldNumControlPoints = connector.controlPoints?.length || 0;
    if (routePoints.length === 0) {
      // Can't route because objects are overlapping. Just do simple routing.
      connector.controlPoints = this._simpleOrthogonalRouting(connector);
    } else {
      connector.controlPoints = routePoints;
    }

    if (oldNumControlPoints !== connector.controlPoints.length) {
      connector.createControls();
    }
  }

  // Returns true if the segment is horizontal. When recursing we only move
  // in one direction in case the whole line is conincident.
  static segmentHorizontal(
    connector: Connector,
    index: number,
    moveStep: number | undefined = undefined
  ) {
    const points =
      connector.lineRouting === ConnectorRouting.ROUTING_ORTHOGONAL_MANUAL
        ? connector.points
        : connector.controlPoints;
    const fromPoint = points[index];
    const toPoint = points[index + 1];

    if (index < 0 || index >= points.length - 1) {
      // This should never happen.
      return true;
    }

    if (fromPoint.x === toPoint.x && fromPoint.y === toPoint.y) {
      // If the segment is zero length, use the direction of the previous or
      // next segment.
      if (moveStep) {
        return !this.segmentHorizontal(connector, index + moveStep, moveStep);
      } else if (index === 0) {
        return !this.segmentHorizontal(connector, index + 1, 1);
      } else {
        return !this.segmentHorizontal(connector, index - 1, -1);
      }
    } else if (fromPoint.x === toPoint.x) {
      return false;
    } else {
      return true;
    }
  }

  // Determines which direction (left, right, up down) an orthorgonal line is
  // pointing
  static endpointDirection(
    connector: Connector,
    whichEnd: ConnectorEnd
  ): MenuDirection {
    const points = connector.controlPoints;
    let p0: IPoint, p1: IPoint;
    let index: number;
    if (whichEnd === ConnectorEnd.CONNECTOR_START) {
      p0 = points[0];
      p1 = points[1];
      index = 0;
    } else {
      p1 = points[points.length - 1];
      p0 = points[points.length - 2];
      index = points.length - 2;
    }

    if (this.segmentHorizontal(connector, index)) {
      return p1.x > p0.x ? 'right' : 'left';
    } else {
      return p1.y > p0.y ? 'down' : 'up';
    }
  }

  static _normalToSide(angle: number, normal: IPoint) {
    // Rotate the normal by the angle.
    normal = fabric.util.rotateVector(normal, (angle * Math.PI) / 180);

    if (Math.abs(normal.x) > Math.abs(normal.y)) {
      return normal.x > 0 ? 'right' : 'left';
    } else {
      return normal.y > 0 ? 'bottom' : 'top';
    }
  }

  static _closestSide(thisEnd: IPoint, otherEnd: IPoint) {
    const dx = otherEnd.x - thisEnd.x;
    const dy = otherEnd.y - thisEnd.y;

    if (Math.abs(dx) > Math.abs(dy)) {
      if (dx > 0) {
        return 'right';
      } else {
        return 'left';
      }
    } else {
      if (dy > 0) {
        return 'bottom';
      } else {
        return 'top';
      }
    }
  }

  static _simpleOrthogonalRouting(connector: Connector) {
    const route: IPoint[] = [];
    route.push(connector.points[0]);
    for (let index = 0; index < connector.points.length - 1; index++) {
      const fromPoint = connector.points[index];
      const toPoint = connector.points[index + 1];

      route.push({ x: fromPoint.x, y: toPoint.y });

      route.push(toPoint);
    }

    return route;
  }

  static pointNearLineSegment(
    connector: Connector,
    point: IPoint,
    index: number,
    radius: number
  ) {
    for (
      let cpIndex = 0;
      cpIndex < connector.controlPoints.length - 1;
      cpIndex++
    ) {
      if (
        pointNearLine(
          point,
          connector.controlPoints[cpIndex],
          connector.controlPoints[cpIndex + 1],
          radius
        )
      ) {
        return true;
      }
    }
    return false;
  }

  static midPoint(connector: Connector, afterIndex: number) {
    return {
      x:
        (connector.controlPoints[afterIndex].x +
          connector.controlPoints[afterIndex + 1].x) /
        2,
      y:
        (connector.controlPoints[afterIndex].y +
          connector.controlPoints[afterIndex + 1].y) /
        2,
    };
  }

  static pointOnLine(connector: Connector, t: number) {
    return RouterStraight.pointOnLine(connector, t);
  }

  static closestPointOnPath(connector: Connector, point: IPoint) {
    return RouterStraight.closestPointOnPath(connector, point);
  }

  static drawLine(connector: Connector, ctx: CanvasRenderingContext2D) {
    const len = connector.controlPoints.length;
    const x = connector.pathOffset.x;
    const y = connector.pathOffset.y;
    let pointFrom = connector.controlPoints[0];

    ctx.moveTo(pointFrom.x - x, pointFrom.y - y);
    for (let i = 1; i < len; i++) {
      const point = connector.controlPoints[i];
      const pointTo = connector.controlPoints[i + 1];

      if (i < len - 1) {
        const radius = Math.min(
          DEFAULT_CORNER_RADIUS,
          Math.hypot(point.x - pointFrom.x, point.y - pointFrom.y) / 2,
          Math.hypot(point.x - pointTo.x, point.y - pointTo.y) / 2
        );
        ctx.arcTo(
          point.x - x,
          point.y - y,
          pointTo.x - x,
          pointTo.y - y,
          radius
        );
      } else {
        ctx.lineTo(point.x - x, point.y - y);
      }

      /* Uncomment to debug control points
      ctx.strokeStyle = 'red';
      ctx.font = '4px serif';
      ctx.strokeRect(point.x - x, point.y - y, 3, 3);
      */

      pointFrom = point;
    }
  }

  static arrowHeadLine(connector: Connector, whichEnd: ConnectorEnd) {
    if (whichEnd === ConnectorEnd.CONNECTOR_START) {
      return {
        from: connector.controlPoints[1],
        to: connector.points[0],
      };
    } else {
      return {
        from: connector.controlPoints[connector.controlPoints.length - 2],
        to: connector.points[connector.points.length - 1],
      };
    }
  }
}

export default RouterOrthogonal;
